import { useNavigate } from "react-router-dom";
import { ArrowLeft, Scale } from "lucide-react";
import { LOGO_URL, BRAND, TAGLINE } from "@/lib/brand";

const UPDATED = "June 13, 2026";

const SECTIONS = [
  {
    h: "1. No Guarantee of Results",
    p: [
      "Love Financial Solutions provides identity-theft dispute management and credit-report organization tools. We do NOT guarantee any specific outcome, including but not limited to the removal, blocking, or deletion of any account, tradeline, or inquiry, any increase in your credit score, or any particular response from a credit bureau, furnisher, or creditor.",
      "Results depend on factors outside our control, including the accuracy of the information you provide, the responses of the credit bureaus and furnishers, and applicable law. Nothing on this site is legal advice, and no attorney-client relationship is created by using this service.",
    ],
  },
  {
    h: "2. Required Documentation You Must Upload",
    p: [
      "To use the dispute and tracking features you agree to upload and maintain the following documentation for each consumer profile:",
    ],
    list: [
      "Your credit reports from the applicable bureaus (Equifax, Experian, and/or TransUnion).",
      "A valid, government-issued photo ID (e.g., driver's license, state ID, or passport).",
      "Your Social Security card (or acceptable proof of your Social Security number).",
      "Verification of your current address (e.g., a utility bill, bank statement, or lease dated within the last 60 days).",
    ],
  },
  {
    h: "3. Keeping Documentation Up to Date",
    p: [
      "You are responsible for keeping all uploaded documentation current, accurate, and complete. If your address, identity documents, or credit reports change, you must promptly upload updated versions. Disputes and letters generated from outdated or inaccurate documentation may be rejected by the bureaus, and we are not responsible for delays or failures resulting from missing or stale documentation.",
    ],
  },
  {
    h: "4. Your Representations — True & Accurate Information",
    p: [
      "You represent, warrant, and certify that all information, statements, and documents you provide to Love Financial Solutions are true, complete, and accurate to the best of your knowledge, and that they belong to you or to a consumer you are lawfully authorized to represent. You will not misrepresent your identity or submit information you know or believe to be false. You understand that all dispute correspondence generated by this service relies entirely on the accuracy of what you provide.",
    ],
  },
  {
    h: "5. Hold Harmless, Release & Indemnification",
    p: [
      "To the fullest extent permitted by law, you agree to release, indemnify, defend, and hold harmless Love Financial Solutions and its owners, officers, employees, contractors, and affiliates (the \"Released Parties\") from and against any and all claims, demands, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to: (a) the information and documents you submit; (b) your use of the service; (c) any dispute, letter, or communication sent to a bureau, furnisher, or creditor based on information you provided; and (d) any breach of these Terms.",
      "The Released Parties are not liable for any indirect, incidental, consequential, or punitive damages, or for any outcome of the dispute process. The service is provided \"as is\" and \"as available\" without warranties of any kind, and you use it at your own risk.",
    ],
  },
  {
    h: "6. Accuracy & Lawful Use",
    p: [
      "You agree not to submit any information you know to be false, and not to dispute information you know to be accurate. Submitting fraudulent identity-theft claims may violate federal and state law.",
    ],
  },
  {
    h: "7. Data & Security",
    p: [
      "Documents you upload are stored to enable the dispute process. You are responsible for the accounts and credentials you use to access the service. Do not share your login. We take reasonable measures to protect your data but cannot guarantee absolute security.",
    ],
  },
  {
    h: "8. Acceptance",
    p: [
      "By creating an account or using Love Financial Solutions, you acknowledge that you have read, understood, and agree to these Terms & Conditions, including that results are not guaranteed, that you must provide and maintain the required documentation described above, that the information you provide is true and accurate, and that you agree to the hold harmless, release, and indemnification provisions.",
    ],
  },
];

export default function Terms() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" data-testid="terms-page">
      <header className="h-20 border-b border-neutral-200 glass sticky top-0 z-20 flex items-center justify-between px-4 sm:px-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-neutral-600 hover:text-[#C1121F] transition-colors" data-testid="terms-back-button">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center gap-3">
          <span className="bg-white rounded-lg p-1.5"><img src={LOGO_URL} alt={BRAND} className="h-8 w-auto object-contain" /></span>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-sm">{BRAND}</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#C1121F]">{TAGLINE}</div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#C1121F]/40 bg-[#C1121F]/10 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-[#9B0E18]">
          <Scale className="h-3.5 w-3.5" /> Legal
        </div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Terms &amp; Conditions</h1>
        <p className="mt-3 text-neutral-500">Last updated {UPDATED}</p>

        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
          <strong>Important:</strong> We do not guarantee results. Using this service requires you to upload and keep
          current your credit reports, a government-issued ID, your Social Security card, and verification of address.
        </div>

        <div className="mt-8 space-y-8">
          {SECTIONS.map((s, i) => (
            <section key={i}>
              <h2 className="font-display text-xl font-bold text-neutral-900">{s.h}</h2>
              {s.p.map((para, j) => <p key={j} className="mt-3 text-neutral-600 leading-relaxed">{para}</p>)}
              {s.list && (
                <ul className="mt-3 space-y-2">
                  {s.list.map((li, k) => (
                    <li key={k} className="flex items-start gap-3 text-neutral-600">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#C1121F] shrink-0" />{li}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <p className="mt-10 text-xs text-neutral-400">
          {BRAND} provides educational dispute-management software and is not a law firm and does not provide legal advice.
        </p>
      </div>
    </div>
  );
}
